/* eslint-disable max-lines */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import { AnalyticsStore, ContextStore, ExperimentStore, QueryCombiner, RouteStore, SerializedData } from 'bernie-core';
import { ErrorBoundary, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PropertyOffers } from '@shared-ui/lodging-property-offers';
import { RefTarget } from '@shared-ui/ref-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel, singleUseQueryParams } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { useRefProvider } from 'src/common/stores/contexts/ref-provider';

import { AlternateDatesCard } from 'components/common/schema-types/alternate-dates-card';
import { DateRange } from 'models';

import { UitkSpacing } from 'uitk-react-spacing';

import { multiItemCompleteSearchCriteria } from 'components/multi-item/utils';

import { AVAILABILITY_HEADER_REF_NAME, ListingsHeader } from './listings-header';
import { FocusTarget } from 'components/common/focus/focus-target';
import { ListingsError } from './listings-error';
import { OffersFilterPills } from 'components/infosite/rooms-and-rates/internals/offers-filter-pills';
import { OffersListComposer } from './offers-list-composer';
import { SearchWizardComposer } from './search-wizard-composer';
import { SharedUiBookByPhoneWrapper } from 'components/infosite/shared-ui/details/book-by-phone-wrapper';
import { SharedUiOffersWrapper } from 'components/infosite/shared-ui/offers/offers-wrapper';
import { SharedUiTravelersLinkWrapper } from 'components/infosite/shared-ui/form/field-wrappers';
import { StickyBookBar } from 'components/infosite/template-components/sticky-book-bar';
import { SWPToggle } from 'components/common/swp-toggle';

export interface RoomsAndRatesLocalProps {
  alternateDatesForMeta?: {
    alternateDateMessage: string;
    alternateDateRange: DateRange;
    url: string;
  };
  analytics?: AnalyticsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  currentHotel?: CurrentHotelStore;
  forceHistoryPushOnGoBack?: () => void;
  // eslint-disable-next-line no-unused-vars
  history?: { push: (url: string) => void };
  multiItem?: MultiItemStore;
  routeStore?: RouteStore;
  searchFormRef: React.RefObject<HTMLElement & { align: string }>;
  recommendedOfferRef: React.RefObject<HTMLElement & { align: string }>;
  roomsAndRatesSectionRef: React.RefObject<HTMLElement & { align: string }>;
}

type LocalizedRoomsAndRatesLocalProps = LocalizedComponentProps & RoomsAndRatesLocalProps;

export const RoomsAndRates: React.FunctionComponent<LocalizedRoomsAndRatesLocalProps> = props => {
  const [searchFormRef, roomsAndRatesSectionRef, recommendedOfferRef] = useRefProvider(
    'searchForm',
    'roomsAndRatesSection',
    'recommendedOffer'
  );

  return (
    <WrappedRoomsAndRates
      searchFormRef={searchFormRef}
      roomsAndRatesSectionRef={roomsAndRatesSectionRef}
      recommendedOfferRef={recommendedOfferRef}
      {...props}
    />
  );
};

@withRouter
@withLocalization
@inject('currentHotel', 'experiment', 'context', 'routeStore', 'shortlist', 'analytics', 'multiItem')
@observer
// This should not be exported, but the existing tests rely too much on the implementation details. Do not import this!
export class WrappedRoomsAndRates extends React.Component<LocalizedRoomsAndRatesLocalProps, {}> {
  constructor(props: LocalizedRoomsAndRatesLocalProps) {
    super(props);

    this.handleUpdateResults = this.handleUpdateResults.bind(this);
    this.onUpdateResults = this.onUpdateResults.bind(this);
    this.onUpdateResultsAvailabilityWizard = this.onUpdateResultsAvailabilityWizard.bind(this);
    this.onUpdateAvailabilityTravelers = this.onUpdateAvailabilityTravelers.bind(this);
    this.navigateToHsr = this.navigateToHsr.bind(this);
  }

  private navigateToHsr({ formData }) {
    const { history, currentHotel } = this.props;
    history.push(currentHotel.getUpdatedBackToHsrUrl(formData));
  }

  private onUpdateAvailabilityTravelers(fields: { [name: string]: string }) {
    this.props.currentHotel.setOfferData(fields);
    this.onUpdateResultsAvailabilityWizard(null, this.props.currentHotel.offerSearchData, true);
  }

  private onUpdateResultsAvailabilityWizard(event: Event, offerSearchData: SerializedData, goBack: boolean = true) {
    this.handleUpdateResults(
      event,
      {
        ...this.props.currentHotel.searchCriteria.toJSON(multiItemCompleteSearchCriteria),
        ...SearchQueryAdapter.parseIn(offerSearchData),
      },
      goBack
    );
  }

  private onUpdateResults(event: Event, searchCriteria: SearchCriteriaModel, goBack: boolean = true) {
    this.handleUpdateResults(event, searchCriteria.toJSON(multiItemCompleteSearchCriteria), goBack, (prevQuery, newQuery) => newQuery);
  }

  private handleUpdateResults(event: Event, criteria: SerializedData, goBack: boolean = true, queryCombinerFunction?: QueryCombiner) {
    const { context, l10n, routeStore } = this.props;

    if (event) {
      event.preventDefault();
    }

    const keysToKeepFromOriginalQuery = key => !key.startsWith('pwaDialog') && !key.startsWith('rm') && !singleUseQueryParams.includes(key);

    const params = { ...criteria, localDateFormat: context.localDateFormat };

    const queryString = routeStore.mergeQueryString(
      routeStore.search,
      InfositeQueryAdapter.parseOut(params, l10n),
      keysToKeepFromOriginalQuery
    );

    if (typeof this.props.forceHistoryPushOnGoBack === 'function') {
      this.props.forceHistoryPushOnGoBack();
    }

    if (goBack) {
      routeStore.rewriteHistory({ search: queryString }, -1);
    } else {
      routeStore.updateSearch(queryString, undefined, true, queryCombinerFunction);
    }
  }

  public componentDidMount() {
    const { currentHotel } = this.props;
    const { searchCriteria } = currentHotel;

    const { startDate, endDate, adults, children } = SearchQueryAdapter.parseOut(searchCriteria);
    // 0 - children from search is undefined
    const childrenFormatted = typeof children === 'undefined' ? '' : children;

    // set offer data on mount
    currentHotel.setOfferData({ startDate, endDate, adults, children: childrenFormatted });
  }

  /* tslint:disable cyclomatic-complexity */
  public render() {
    const {
      alternateDatesForMeta,
      analytics,
      context,
      currentHotel,
      l10n,
      multiItem,
      routeStore,
      forceHistoryPushOnGoBack,
      experiment,
      searchFormRef,
    } = this.props;

    const { offers, searchCriteria, bexApiPropertyInfoOffersResponse, loadingOffers, defaultedDates } = currentHotel;
    const { rooms, alternateDatesCard } = offers || {};

    const verticalListings =
      bexApiPropertyInfoOffersResponse?.offers?.experimental?.ratePlanCategorizationExperimentalFeature.verticalListings;

    const listings = bexApiPropertyInfoOffersResponse?.offers?.listings || verticalListings;
    const pointsToggleModel = offers?.loyaltyDiscount;

    const soldOut = Boolean((!rooms || rooms.length === 0) && (!listings || listings.length === 0));
    const isMultiItem = multiItem && multiItem.isPackageShoppingPath();

    const className = [
      'rooms-and-rates s-t-margin-three m-t-margin-three s-x-padding-three',
      'm-x-padding-zero l-x-padding-zero xl-x-padding-zero',
    ].join(' ');

    const searchWizardComposerProps = {
      currentHotel,
      analytics,
      l10n,
      routeStore,
      onUpdateResultsAvailabilityWizard: this.onUpdateResultsAvailabilityWizard,
      searchFormRef,
      RefTarget,
      experiment,
      navigateToHsr: this.navigateToHsr,
      isMultiItem,
      context,
      soldOut,
      onUpdateResults: this.onUpdateResults,
      alternateDatesForMeta,
    };

    const offersListComposerProps = {
      analytics,
      currentHotel,
      routeStore,
      soldOut,
      loadingOffers,
    };

    const SharedUiOffers = (
      <SharedUiOffersWrapper>
        <PropertyOffers.HeaderRight>
          {!isMultiItem && (
            <Viewport>
              <ViewSmall>
                <SharedUiTravelersLinkWrapper handleChange={this.onUpdateAvailabilityTravelers} />
              </ViewSmall>
              <ViewLarge />
            </Viewport>
          )}
        </PropertyOffers.HeaderRight>
        <PropertyOffers.Form>
          <UitkSpacing padding={{ blockend: 'three' }}>
            <div>
              <SearchWizardComposer {...searchWizardComposerProps} />
              {pointsToggleModel && (
                <div className="all-y-padding-two">
                  <SWPToggle
                    model={pointsToggleModel}
                    searchCriteria={searchCriteria}
                    routeStore={routeStore}
                    forceHistoryPushOnGoBack={forceHistoryPushOnGoBack}
                  />
                </div>
              )}
              <OffersFilterPills pills={offers?.filterPills} onClick={forceHistoryPushOnGoBack} />
            </div>
          </UitkSpacing>
        </PropertyOffers.Form>
      </SharedUiOffersWrapper>
    );

    return (
      <ErrorBoundary label="hotel.information.rooms.roomsAndRates">
        <FocusTarget targetKey="searchForm">
          <RefTarget name="Offers" className={className} dataStid="rooms-and-rates">
            <RefTarget name={AVAILABILITY_HEADER_REF_NAME} tabIndex={0}>
              <UitkSpacing margin={{ blockstart: 'six' }}>
                <section ref={this.props.roomsAndRatesSectionRef}>
                  <UitkSpacing padding={{ blockend: 'three' }}>
                    <SharedUiBookByPhoneWrapper />
                  </UitkSpacing>
                  <Experiment name="PDP_Shared_UI_Group4">
                    <ExperimentControl>
                      {verticalListings ? (
                        SharedUiOffers
                      ) : (
                        <>
                          <ListingsHeader
                            RightComponent={
                              !isMultiItem && (
                                <Viewport>
                                  <ViewSmall>
                                    <SharedUiTravelersLinkWrapper handleChange={this.onUpdateAvailabilityTravelers} />
                                  </ViewSmall>
                                  <ViewLarge />
                                </Viewport>
                              )
                            }
                          />
                          <ListingsError />

                          {!defaultedDates && soldOut && alternateDatesCard && (
                            <div className="all-b-padding-two">
                              <AlternateDatesCard model={alternateDatesCard} />
                            </div>
                          )}

                          <SearchWizardComposer {...searchWizardComposerProps} />

                          {soldOut && defaultedDates && alternateDatesCard && (
                            <div className="all-t-padding-two">
                              <AlternateDatesCard model={alternateDatesCard} />
                            </div>
                          )}

                          {pointsToggleModel && (
                            <div className="all-y-padding-two">
                              <SWPToggle
                                model={pointsToggleModel}
                                searchCriteria={searchCriteria}
                                routeStore={routeStore}
                                forceHistoryPushOnGoBack={forceHistoryPushOnGoBack}
                              />
                            </div>
                          )}
                          <OffersFilterPills pills={offers?.filterPills} onClick={forceHistoryPushOnGoBack} />

                          <OffersListComposer {...offersListComposerProps} />
                        </>
                      )}
                    </ExperimentControl>
                    <ExperimentVariant bucket={1}>{SharedUiOffers}</ExperimentVariant>
                  </Experiment>

                  <Viewport>
                    <ViewSmall>
                      <StickyBookBar />
                    </ViewSmall>
                    <ViewLarge>
                      <React.Fragment />
                    </ViewLarge>
                  </Viewport>
                </section>
              </UitkSpacing>
            </RefTarget>
          </RefTarget>
        </FocusTarget>
      </ErrorBoundary>
    );
  }
}

export default RoomsAndRates;
